import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@mui/material';
import { logEvent } from '~/app/application/shared/lib/tag';
import BaseButton from '~/app/core/presentation/components/base-button/base-button';
import VerificationCodeInput from '~/app/core/presentation/components/verification-code-input/verification-code-input';
import { useAuth } from '~/app/core/presentation/hooks/use-auth';

import { RegisterRoutes } from '../../../../../routes/routes';
import { useConfirmEmailCodeMutation } from '../../../../../store/hooks';
import { useRegisterUserDataStore } from '../../../../../store/slice/register-user-data-slice';
import { SignUpFormDataProps } from '../../form-register';
import { RegisterFormWizardTitle } from '../../form-register-styles';
import {
  AlertBox,
  ValidadeCodeContainer,
  VerificationBox
} from '../../validate-code-step/validate-code-step-styles';
import { CodeValidator } from '../../validate-code-step/validators/code-validator';

export type ConfirmEmailStepProps = {
  formData: SignUpFormDataProps;
  comeBack: (data: { onClick: () => void }) => void;
};

type codeModel = {
  code: string[];
};

const ConfirmEmailStep = ({ formData, comeBack }: ConfirmEmailStepProps) => {
  const { t } = useTranslation(['validate-code-step', 'errors', 'register']);
  const [confirmEmailCode, { isLoading }] = useConfirmEmailCodeMutation();
  const { useLogin } = useAuth();

  const [isError, setIsError] = useState(false);
  const navigate = useNavigate();
  const { setRegisterState } = useRegisterUserDataStore();

  const confirmEmailCodeRequest = (code: string) => {
    confirmEmailCode({
      email: formData.user.email,
      code
    })
      .unwrap()
      .then((response) => {
        // Salvar tokens
        useLogin({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken
        });

        setRegisterState({
          isEnterpriseContact: false,
          user: {
            active: true,
            email: formData.user.email,
            firstName: formData.user.firstName,
            lastName: formData.user.lastName,
            phone: formData.user.phone
          },
          carQuantity: formData.user.carQuantity,
          isAuthenticated: true
        });

        logEvent({
          action: 'OnSubmit',
          label: 'step01_cadastro_email_confirmado',
          category: 'Confirmacao Email'
        });

        // Redirecionar para company data
        navigate(RegisterRoutes.COMPANY_DATA);
      })
      .catch(() => {
        setIsError(true);
        setTimeout(() => {
          setIsError(false);
        }, 10000);
      });
  };

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid }
  } = useForm<{ code: string[] }>({
    resolver: CodeValidator,
    mode: 'onChange'
  });

  const handleSubmitForm = (data: codeModel) => {
    confirmEmailCodeRequest(data.code.join(''));
  };

  return (
    <ValidadeCodeContainer
      component='form'
      onSubmit={handleSubmit(handleSubmitForm)}
    >
      <RegisterFormWizardTitle>
        {t('register:confirmEmail')}
      </RegisterFormWizardTitle>
      <div>{t('enterCode')}</div>
      {isError && (
        <AlertBox>
          <Alert severity='error'>{t('errors:invalidCode')}</Alert>
        </AlertBox>
      )}
      <VerificationBox>
        <VerificationCodeInput control={control} />
      </VerificationBox>
      <BaseButton
        disableElevation
        type='submit'
        variant='contained'
        color='primary'
        disabled={!isDirty || !isValid}
        isLoading={isLoading}
      >
        {t('register:next')}
      </BaseButton>

      <BaseButton
        disableElevation
        variant='text'
        color='primary'
        onClick={() => comeBack({ onClick: () => null })}
        sx={{ mt: 2 }}
      >
        {t('register:back')}
      </BaseButton>
    </ValidadeCodeContainer>
  );
};

export default ConfirmEmailStep;
